import { useEffect, useRef, useState, type FC, type ReactNode } from 'react';
import {
  Content,
  ExpandableSearch,
  Header,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderMenuButton,
  HeaderName,
  SkipToContent,
} from '@carbon/react';
import { Notification, NotificationNew, UserAvatar } from '@carbon/icons-react';

import { IconeDaNyta } from '../marcas/MarcaDaNyta';
import { MarcaMaestra } from '../marcas/MarcaMaestra';
import { useDoPonto } from '../padroes/useDoMd';
import {
  NavegacaoDoPerfil, type ModuloDaNavegacao, type PerfilDaNavegacao, type PlanoDaNavegacao,
} from './NavegacaoDoPerfil';
import { PainelDaConta, type ContaDoPainel } from './PainelDaConta';
import { PainelDeNotificacoes } from './PainelDeNotificacoes';
import { PainelDePerfis } from './PainelDePerfis';
import { RailDePerfis, type PerfilDaFaixa } from './RailDePerfis';
import styles from './Casca.module.scss';

// A CASCA DO APP — o UI Shell do Carbon, o mesmo em toda largura e em toda tela.
//
// Header persistente: ☰ (só no celular, abaixo do `md`, com um perfil aberto), a marca e as ações globais, da
// esquerda para a direita: busca (enquanto houver tela antiga que filtra pelo topo), a Nyta (a
// estrela dela, e não um balão de chat), as notificações e a conta. Cada uma abre o SEU painel da
// direita, e um de cada vez: abrir um fecha o outro, e a Nyta é um deles.
//
// À esquerda, do `md` para cima (tablet e computador): a faixa dos perfis (trocar de perfil num
// clique, e ver sempre em qual se está) e o menu do perfil aberto. No tablet (md a lg) o menu fica
// sempre recolhido, só os ícones: aberto, ele tomaria um terço da tela. Do `lg` para cima, a pessoa
// escolhe recolher ou não. No celular, o ☰ abre o menu por cima, e dali "Trocar de perfil" abre a lista.
//
// Desenho puro: tudo o que decide vem por props (a lógica mora em src/logica/casca).

export type PainelAberto = 'perfis' | 'conta' | 'notificacoes' | null;

export interface PropsDaCasca {
  rota: string;
  abrirPerfis: () => void;
  /** Só enquanto houver tela antiga que filtra pelo topo. */
  busca?: { termo: string; placeholder: string; aoMudar: (t: string) => void; aoLimpar: () => void };
  /** A Nyta é um painel da direita: a casca sabe se ela está aberta para fechar os outros. */
  nyta?: { aberta: boolean; abrir: () => void; fechar: () => void };
  naoLidas: number;
  /** O conteúdo do painel do sino (só é montado aberto); `fechar` fecha o painel. */
  notificacoes: (fechar: () => void) => ReactNode;
  perfis: PerfilDaFaixa[];
  /** O perfil aberto (o aceso na faixa). */
  perfilAtual?: string;
  abrirPerfil: (id: string) => void;
  criarPerfil: () => void;
  navegacao?: {
    rotulo: string;
    modulos: ModuloDaNavegacao[];
    ativo: (chave: string) => boolean;
    abrir: (chave: string) => void;
    perfil?: PerfilDaNavegacao | null;
    plano?: PlanoDaNavegacao | null;
  };
  /** O menu do perfil recolhido (só os ícones), lembrado por quem usa. */
  menu?: { recolhido: boolean; alternar: () => void };
  conta: ContaDoPainel;
  /** Só para a vitrine e os testes: o painel que já abre aberto. */
  painelInicial?: PainelAberto;
  /** O player global, quando há faixa tocando. */
  player?: ReactNode;
  children?: ReactNode;
}

export const CascaVista: FC<PropsDaCasca> = ({
  rota, abrirPerfis, busca, nyta, naoLidas, notificacoes,
  perfis, perfilAtual, abrirPerfil, criarPerfil, navegacao, menu, conta, painelInicial = null, player, children,
}) => {
  const [navegacaoAberta, setNavegacaoAberta] = useState(false);
  const [painel, setPainel] = useState<PainelAberto>(painelInicial);
  const doLg = useDoPonto('lg');
  const doMd = useDoPonto('md');
  // A faixa dos perfis: do md para cima, fora da lista de perfis (ali a página já é a lista).
  const comFaixa = doMd && perfis.length > 0 && rota.replace(/\/+$/, '') !== '/artists';
  // No tablet o menu é sempre o rail; do lg para cima, como a pessoa deixou.
  const tablet = doMd && !doLg;
  const recolhido = !!navegacao && (tablet || !!menu?.recolhido);

  // Trocar de tela fecha o ☰ e os painéis (a Nyta fica: a conversa acompanha a pessoa).
  const [rotaDeAntes, setRotaDeAntes] = useState(rota);
  if (rota !== rotaDeAntes) { setRotaDeAntes(rota); setNavegacaoAberta(false); setPainel(null); }

  // A Nyta abriu (pelo topo ou por um botão de um módulo): os outros painéis saem.
  const nytaAberta = !!nyta?.aberta;
  useEffect(() => { if (nytaAberta) setPainel(null); }, [nytaAberta]);


  // Quem abriu o painel recebe o foco de volta quando ele fecha (Esc, ou escolher um item), em vez
  // de o foco cair no `body`. O "Trocar de perfil" do menu some com o menu: ali a volta é o ☰.
  const origem = useRef<HTMLElement | null>(null);
  const devolverFoco = () => {
    const volta = origem.current;
    origem.current = null;
    if (volta && document.contains(volta)) requestAnimationFrame(() => volta.focus());
  };
  const alternar = (p: Exclude<PainelAberto, null>, deOnde?: HTMLElement | null) => {
    if (nytaAberta) nyta?.fechar();
    origem.current = deOnde ?? (document.activeElement as HTMLElement | null);
    setPainel((atual) => (atual === p ? null : p));
  };
  const fechar = () => { setPainel(null); devolverFoco(); };

  // Esc fecha o painel aberto.
  useEffect(() => {
    if (!painel) return undefined;
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar(); };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [painel]);

  // Aberta pelo menu, a lista dos perfis leva o foco para o primeiro perfil.
  useEffect(() => {
    if (painel !== 'perfis') return undefined;
    const id = requestAnimationFrame(() => document.querySelector<HTMLElement>('[data-painel-de-perfis] .cds--contained-list-item__content')?.focus());
    return () => cancelAnimationFrame(id);
  }, [painel]);

  // Escolher o perfil que já está aberto não sai da tela: só fecha o que estiver aberto.
  const irParaOPerfil = (id: string) => {
    if (id === perfilAtual) { fechar(); return; }
    setPainel(null);
    abrirPerfil(id);
  };

  return (
    <div
      className={[
        styles.casca,
        navegacao ? styles.comNavegacao : '',
        recolhido ? styles.recolhida : '',
        comFaixa ? styles.comFaixa : '',
        nytaAberta ? styles.comNyta : '',
        player ? styles.comPlayer : '',
      ].filter(Boolean).join(' ')}
      data-casca=''
      data-ui-carbon=''
      data-com-navegacao={navegacao ? '' : undefined}
      data-com-player={player ? '' : undefined}
      data-com-faixa={comFaixa ? '' : undefined}
      data-recolhida={recolhido ? '' : undefined}
    >
      <Header aria-label='Maestra'>
        <SkipToContent href='#conteudo'>Pular para o conteúdo</SkipToContent>
        {navegacao && !doMd && (
          <HeaderMenuButton
            aria-label={navegacaoAberta ? 'Fechar menu do perfil' : 'Abrir menu do perfil'}
            isActive={navegacaoAberta}
            aria-expanded={navegacaoAberta}
            onClick={() => setNavegacaoAberta((v) => !v)}
          />
        )}
        <HeaderName
          href='/artists'
          prefix=''
          className={styles.marca}
          onClick={(e: React.MouseEvent) => { e.preventDefault(); abrirPerfis(); }}
        >
          {/* No celular, só o símbolo: o nome do perfil ao lado precisa do espaço. */}
          <MarcaMaestra forma={!doMd && navegacao?.perfil ? 'simbolo' : 'completa'} />
        </HeaderName>
        {/* No celular não há faixa nem menu à vista: o perfil aberto aparece ao lado da marca. */}
        {!doMd && navegacao?.perfil && <span className={styles.perfilNoTopo}>{navegacao.perfil.nome}</span>}

        <HeaderGlobalBar>
          {busca && (
            <div className={styles.busca}>
              <ExpandableSearch
                size='lg'
                labelText={busca.placeholder}
                placeholder={busca.placeholder}
                value={busca.termo}
                closeButtonLabelText='Limpar busca'
                onChange={(e) => busca.aoMudar(typeof e === 'string' ? e : e.target.value)}
                onClear={busca.aoLimpar}
              />
            </div>
          )}
          {nyta && (
            <HeaderGlobalAction
              aria-label='Nyta, a IA da Maestra'
              aria-expanded={nytaAberta}
              isActive={nytaAberta}
              onClick={() => { if (nytaAberta) nyta.fechar(); else { setPainel(null); nyta.abrir(); } }}
            >
              <IconeDaNyta size={20} />
            </HeaderGlobalAction>
          )}
          <HeaderGlobalAction
            aria-label={naoLidas > 0 ? `Notificações, ${naoLidas} não lidas` : 'Notificações'}
            aria-expanded={painel === 'notificacoes'}
            isActive={painel === 'notificacoes'}
            data-nao-lidas={naoLidas}
            onClick={() => alternar('notificacoes')}
          >
            {naoLidas > 0 ? <NotificationNew size={20} /> : <Notification size={20} />}
          </HeaderGlobalAction>
          <HeaderGlobalAction
            aria-label='Sua conta'
            aria-expanded={painel === 'conta'}
            isActive={painel === 'conta'}
            tooltipAlignment='end'
            onClick={() => alternar('conta')}
          >
            <UserAvatar size={20} />
          </HeaderGlobalAction>
        </HeaderGlobalBar>

        <PainelDeNotificacoes aberto={painel === 'notificacoes'} conteudo={notificacoes(fechar)} />
        {/* No celular, a lista dos perfis abre pelo "Trocar de perfil" do menu. */}
        <PainelDePerfis
          aberto={painel === 'perfis'}
          perfis={perfis}
          atual={perfilAtual}
          aoAbrir={irParaOPerfil}
          aoCriar={() => { setPainel(null); criarPerfil(); }}
        />
        <PainelDaConta aberto={painel === 'conta'} conta={conta} aoEscolher={fechar} />

        {/* A faixa vem antes do menu no DOM: é a ordem em que se vê (e a do Tab). */}
        {comFaixa && (
          <RailDePerfis
            perfis={perfis}
            atual={perfilAtual}
            abrir={irParaOPerfil}
            criar={criarPerfil}
            verTodos={abrirPerfis}
            // Recolher só do lg para cima: no tablet o menu já é sempre o rail.
            menu={navegacao && doLg ? menu : undefined}
          />
        )}

        {navegacao && (
          <NavegacaoDoPerfil
            aberta={navegacaoAberta}
            aoFechar={() => setNavegacaoAberta(false)}
            recolhido={recolhido}
            // Com outros perfis, a lista deles; com um só, a página dos perfis (onde se cria outro).
            trocarDePerfil={perfis.length > 1
              ? { rotulo: 'Trocar de perfil', abrir: () => alternar('perfis', document.querySelector<HTMLElement>('.cds--header__menu-trigger')) }
              : { rotulo: 'Todos os perfis', abrir: abrirPerfis }}
            {...navegacao}
          />
        )}
      </Header>

      <Content id='conteudo' className={styles.conteudo}>
        {children}
      </Content>

      {player}
    </div>
  );
};
